import {
  BIBLE_BOOKS,
  IDEA_ACTIONS,
  parseIdeaContent,
  SERIES_AUDIENCES,
  seriesPlanInputSchema,
  seriesWeekDates,
  type SeriesPlan,
  type SeriesPlanInput,
} from '@church/domain'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useAccess, useServiceAccess } from '../access/useAccess.ts'
import { useApi } from '../api-context.tsx'
import { notify, notifyFailure } from '../feedback.ts'
import { useDebounced } from '../people/usePeople.ts'
import { sermonKeys } from './sermon-keys.ts'
import { useDraft } from './useSermonIdeas.ts'
import { servicesKeys, useServiceTypes } from './useServices.ts'
import { aiKeys, useAiCost, useAiStatus } from './useSuggestions.ts'

/**
 * ForServices' Sermons (docs/15, "Sermon writing"), church-level, for anyone
 * who is Editor on at least one service type: "What have we preached on…?"
 * (search, no AI) and the series planner (an outline from a book or a topic,
 * made into a series and draft plans when someone chooses).
 */

const POLL_MS = 3000

/** Sermons is theirs: Editor somewhere. The planner needs AI too. */
export function useSermonsAccess() {
  const editor = useAccess().hasSomewhere('planning', 'editor')
  const ai = useAiStatus().data?.enabled === true
  return { sermons: editor, planner: editor && ai }
}

// ---- what have we preached on ---------------------------------------------------------------

/** "Oct 4, 2026". */
const longDay = (date: string) =>
  new Date(`${date}T12:00:00Z`).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  })

/** The book choices: Old Testament, then New. */
export const BOOK_CHOICES = [
  {
    label: 'Old Testament',
    books: BIBLE_BOOKS.filter((b) => b.testament === 'old').map((b) => b.name),
  },
  {
    label: 'New Testament',
    books: BIBLE_BOOKS.filter((b) => b.testament === 'new').map((b) => b.name),
  },
] as const

/**
 * The search: words (debounced), a theme, a book — any together — and the
 * results a page at a time, latest first. Empty filters show every sermon.
 */
export function useSermonSearch() {
  const api = useApi()
  const allowed = useSermonsAccess().sermons
  const [q, setQ] = useState('')
  const [theme, setTheme] = useState('')
  const [book, setBook] = useState('')
  const words = useDebounced(q.trim(), 300)
  const filters = { q: words, theme, book }
  const query = useInfiniteQuery({
    queryKey: sermonKeys.search(filters),
    enabled: allowed,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const { data, error } = await api.api.v1.services.sermons.search.get({
        query: {
          ...(filters.q ? { q: filters.q } : {}),
          ...(filters.theme ? { theme: filters.theme } : {}),
          ...(filters.book ? { book: filters.book } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        },
      })
      if (error) throw error
      return data
    },
    getNextPageParam: (last) => last.nextCursor,
    placeholderData: (previous) => previous,
  })
  const themes = useQuery({
    queryKey: sermonKeys.themes(),
    enabled: allowed,
    queryFn: async () => {
      const { data, error } = await api.api.v1.services.sermons.themes.get()
      if (error) throw error
      return data
    },
    staleTime: 60_000,
  })
  const results = (query.data?.pages ?? [])
    .flatMap((page) => page.items)
    .map((item) => ({
      ...item,
      day: longDay(item.date),
      /** "Sunday Morning · Sermon on the Mount" (the service type, then its series). */
      where: [item.serviceTypeName, item.series?.name].filter(Boolean).join(' · '),
      /** The snippet's match split out, to set in bold. */
      snippetParts: item.snippet ? splitSnippet(item.snippet) : null,
    }))
  const filtered = words !== '' || theme !== '' || book !== ''
  return {
    allowed,
    q,
    setQ,
    theme,
    setTheme,
    book,
    setBook,
    clear: () => {
      setQ('')
      setTheme('')
      setBook('')
    },
    filtered,
    query,
    results,
    themes: themes.data ?? [],
    /** Searching (words typed, results not yet in). */
    searching: q.trim() !== words || (query.isFetching && !query.isFetchingNextPage),
    empty: query.isSuccess && results.length === 0,
    emptyLine: filtered
      ? 'No sermon matches. Try fewer words, or another theme or book.'
      : 'No sermons yet. A plan’s sermon shows here once it is added.',
  }
}

export type SermonResult = ReturnType<typeof useSermonSearch>['results'][number]

/** "…the «salt» of the earth…" → the parts, each marked whether it matched. */
export function splitSnippet(snippet: string) {
  return snippet
    .split(/(«[^»]*»)/)
    .filter(Boolean)
    .map((part) =>
      part.startsWith('«')
        ? { text: part.slice(1, -1), match: true }
        : { text: part, match: false },
    )
}

// ---- the series planner ----------------------------------------------------------------------------

export function useSeriesPlans() {
  const api = useApi()
  const allowed = useSermonsAccess().sermons
  const query = useInfiniteQuery({
    queryKey: sermonKeys.seriesPlans(),
    enabled: allowed,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const { data, error } = await api.api.v1.services['series-plans'].get({
        query: pageParam ? { cursor: pageParam } : {},
      })
      if (error) throw error
      return data
    },
    getNextPageParam: (last) => last.nextCursor,
    refetchInterval: (q) =>
      q.state.data?.pages.some((page) => page.items.some((item) => item.state === 'processing'))
        ? POLL_MS
        : false,
  })
  const outlines = (query.data?.pages ?? [])
    .flatMap((page) => page.items)
    .map((item) => ({
      ...item,
      plan: item.state === 'ready' ? parseIdeaContent('series_plan', item.content) : null,
      /** "Ruth · 4 weeks · The whole church". */
      asked: item.input
        ? [
            item.input.source === 'book' ? item.input.book : item.input.topic,
            `${item.input.weeks} weeks`,
            item.input.audience,
          ].join(' · ')
        : '',
    }))
  return { query, outlines }
}

export type SeriesOutline = ReturnType<typeof useSeriesPlans>['outlines'][number]

export const SERIES_PLAN_DEFAULTS = {
  source: 'book',
  book: '',
  topic: '',
  weeks: 6,
  audience: SERIES_AUDIENCES[0],
  notes: '',
} as const

/** Ask for an outline, change one, remove one, and make a series from one. */
export function useSeriesPlanner() {
  const api = useApi()
  const queryClient = useQueryClient()
  const cost = useAiCost([IDEA_ACTIONS.series_plan])
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: aiKeys.status() })
    return queryClient.invalidateQueries({ queryKey: sermonKeys.seriesPlans() })
  }
  const outline = (id: string) => api.api.v1.services['series-plans']({ id })
  const request = useMutation({
    mutationFn: async (input: SeriesPlanInput) => {
      const { data, error } = await api.api.v1.services['series-plans'].post(input)
      if (error) throw error
      return data
    },
    onSuccess: () => void refresh(),
    onError: (error) => notifyFailure(error, undefined, 'Couldn’t outline the series'),
  })
  const save = useMutation({
    mutationFn: async (input: { id: string; content: SeriesPlan }) => {
      const { data, error } = await outline(input.id).put({ content: input.content })
      if (error) throw error
      return data
    },
    onSuccess: () => {
      notify({ tone: 'success', title: 'Saved your changes' })
      void refresh()
    },
    onError: (error) => notifyFailure(error, undefined, 'Couldn’t save it'),
  })
  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await outline(id).delete()
      if (error) throw error
    },
    onSuccess: () => void refresh(),
    onError: (error) => notifyFailure(error, undefined, 'Couldn’t remove it'),
  })
  const createSeries = useMutation({
    mutationFn: async (input: {
      id: string
      name: string
      description: string | null
      serviceTypeId: string | null
      startOn: string | null
    }) => {
      const { id, ...body } = input
      const { data, error } = await outline(id).series.post(body)
      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      notify({
        tone: 'success',
        title: 'Made the series',
        description:
          data.planIds.length > 0
            ? `With ${data.planIds.length} draft plans, each with its sermon’s title and passage.`
            : 'Add its plans when you are ready.',
      })
      void refresh()
      void queryClient.invalidateQueries({ queryKey: servicesKeys.all() })
    },
    onError: (error) => notifyFailure(error, undefined, 'Couldn’t make the series'),
  })
  return { cost, request, save, remove, createSeries, schema: seriesPlanInputSchema }
}

/** An outline's draft (its names, description and weeks), saved whole. */
export function useSeriesPlanDraft(id: string, content: SeriesPlan | null) {
  const draft = useDraft<SeriesPlan>(content)
  const planner = useSeriesPlanner()
  return {
    ...draft,
    pending: planner.save.isPending,
    save: () => {
      if (!draft.draft) return
      const parsed = parseIdeaContent('series_plan', draft.draft)
      if (!parsed) {
        notify({
          tone: 'error',
          title: 'Something is empty',
          description: 'Every week needs a title, a passage, a big idea and a summary.',
        })
        return
      }
      planner.save.mutate({ id, content: parsed }, { onSuccess: draft.cancel })
    },
  }
}

/** The days a series' draft plans would fall on, to show before they are made. */
export const seriesDays = (startOn: string, weeks: number) =>
  startOn ? seriesWeekDates(startOn, weeks).map(longDay) : []

/** The next Sunday from today (an ISO day): where a series' first week starts by default. */
function nextSunday(now = new Date()): string {
  const day = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()))
  day.setUTCDate(day.getUTCDate() + ((7 - day.getUTCDay()) % 7 || 7))
  return day.toISOString().slice(0, 10)
}

/**
 * Create series from an outline: its name (one of the ideas, or their own),
 * its description, and — if they choose — a draft plan a week in a service
 * type they are Editor on, from a first day (next Sunday by default).
 */
export function useSeriesFromOutline(outline: SeriesOutline | null) {
  const types = useServiceTypes()
  const access = useServiceAccess()
  const planner = useSeriesPlanner()
  const plan = outline?.plan ?? null
  const [name, setName] = useState<string | null>(null)
  const [description, setDescription] = useState<string | null>(null)
  const [withPlans, setWithPlans] = useState(true)
  const [serviceTypeId, setServiceTypeId] = useState<string | null>(null)
  const [startOn, setStartOn] = useState(nextSunday())
  const choices = (types.data ?? []).filter((t) => access.can(t.id, 'editor'))
  const typeId = serviceTypeId ?? choices[0]?.id ?? ''
  const chosenName = name ?? plan?.names[0] ?? ''
  const plansToo = withPlans && typeId !== '' && startOn !== ''
  return {
    name: chosenName,
    setName,
    description: description ?? plan?.description ?? '',
    setDescription,
    withPlans: withPlans && choices.length > 0,
    setWithPlans,
    canMakePlans: choices.length > 0,
    serviceTypes: choices.map((t) => ({ id: t.id, name: t.name })),
    serviceTypeId: typeId,
    setServiceTypeId,
    startOn,
    setStartOn,
    /** The days its plans would fall on. */
    days: plansToo && plan ? seriesDays(startOn, plan.weeks.length) : [],
    pending: planner.createSeries.isPending,
    ready: outline !== null && plan !== null && chosenName.trim() !== '',
    submit: (done: () => void) => {
      if (!outline || !plan || !chosenName.trim()) return
      planner.createSeries.mutate(
        {
          id: outline.id,
          name: chosenName.trim(),
          description: (description ?? plan.description).trim() || null,
          serviceTypeId: plansToo ? typeId : null,
          startOn: plansToo ? startOn : null,
        },
        { onSuccess: done },
      )
    },
  }
}
